// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * #100 version handshake — web twin of mobile/src/lib/amySession.ts (same
 * decision, same stored shape; keep the two in sync):
 *
 * - `conversationId` is STABLE: minted once, kept through every rotation
 *   and deploy; AgentCore Memory keys chat history by it, so upgrades are
 *   invisible — same chat, full context. A legacy stored entry ({id,
 *   lastUsedAt}) adopts its old session id AS the conversationId, so
 *   pre-#100 history is never orphaned.
 * - `runtimeSessionId` pins the deployed container VERSION and rotates
 *   when (a) none stored, (b) idle > 20 min (the platform session is dead
 *   anyway), or (c) the conversation's bound version differs from the
 *   currently deployed Amy behavior version (GET /v1/amy/version) — the
 *   next turn then runs on the current container, mandatorily.
 * - `currentVersion === null` (version endpoint unreachable) never blocks
 *   a send: the turn proceeds on the existing session; the next successful
 *   version read rotates. Safe retry over hard failure — and never any
 *   user-facing version talk.
 */

export interface AmySessionStore {
  get(key: string): string | null
  set(key: string, value: string): void
  remove(key: string): void
}

const SESSION_KEY_PREFIX = "bb_amy_session_"
/** AgentCore InvokeAgentRuntime hard constraint (≥33 chars). */
export const MIN_RUNTIME_SESSION_ID_LENGTH = 33
export const SESSION_IDLE_ROTATE_MS = 20 * 60 * 1000

interface StoredAmySession {
  id?: string
  conversationId?: string
  boundVersion?: string | null
  lastUsedAt?: number
}

export interface AmySession {
  conversationId: string
  runtimeSessionId: string
  /** True when THIS call rotated a live runtime session because of a version change (telemetry). */
  rebound: boolean
  boundVersion: string | null
  /** What the conversation was bound to BEFORE this call (telemetry). */
  previousBoundVersion: string | null
}

function freshId(): string {
  return `bb-amy-${crypto.randomUUID()}`
}

export function browserSessionStore(): AmySessionStore {
  return {
    get: (key) => {
      try {
        return window.sessionStorage.getItem(key)
      } catch {
        return null
      }
    },
    set: (key, value) => {
      try {
        window.sessionStorage.setItem(key, value)
      } catch {
        /* storage unavailable — session lives for this send only */
      }
    },
    remove: (key) => {
      try {
        window.sessionStorage.removeItem(key)
      } catch {
        /* best-effort */
      }
    },
  }
}

export function amySessionFor(
  store: AmySessionStore,
  userKey: string,
  currentVersion: string | null,
  now = Date.now(),
  durableConversationId?: string,
): AmySession {
  const key = `${SESSION_KEY_PREFIX}${userKey}`
  let stored: StoredAmySession = {}
  try {
    const raw = store.get(key)
    if (raw) stored = JSON.parse(raw) as StoredAmySession
  } catch {
    stored = {}
  }
  // #106: when the caller supplies the SERVER-registered durable id, it is
  // authoritative — the stored value is only the per-tab echo of it. A
  // caller without one (older wiring, tests) keeps the legacy behavior:
  // stored id, else legacy runtime-id adoption, else a fresh mint.
  const conversationId =
    typeof durableConversationId === "string" && durableConversationId.length > 0
      ? durableConversationId
      : typeof stored.conversationId === "string" && stored.conversationId.length > 0
        ? stored.conversationId
        : typeof stored.id === "string" && stored.id.length >= MIN_RUNTIME_SESSION_ID_LENGTH
          ? stored.id // legacy migration: the old session id becomes the history key
          : freshId()
  // A conversation switch (server adopted a different thread, or a new
  // chat) must not keep a runtime pinned to the old thread's turn state.
  const conversationChanged =
    typeof stored.conversationId === "string" &&
    stored.conversationId.length > 0 &&
    stored.conversationId !== conversationId

  const idle =
    typeof stored.lastUsedAt !== "number" || now - stored.lastUsedAt >= SESSION_IDLE_ROTATE_MS
  const hasRuntime =
    typeof stored.id === "string" && stored.id.length >= MIN_RUNTIME_SESSION_ID_LENGTH
  const versionMismatch = currentVersion !== null && (stored.boundVersion ?? null) !== currentVersion

  const mustRotate = !hasRuntime || idle || versionMismatch || conversationChanged
  const runtimeSessionId = mustRotate ? freshId() : (stored.id as string)
  const rebound = versionMismatch && hasRuntime && !idle
  const boundVersion = currentVersion ?? stored.boundVersion ?? null

  store.set(key, JSON.stringify({ id: runtimeSessionId, conversationId, boundVersion, lastUsedAt: now }))
  return {
    conversationId,
    runtimeSessionId,
    rebound,
    boundVersion,
    previousBoundVersion: stored.boundVersion ?? null,
  }
}

/**
 * Error-retry rotation (owner incident 2026-08-30): clear the RUNTIME id
 * so the transparent retry starts a fresh session on the current
 * deployment — the conversationId survives, so the retried turn keeps
 * full history.
 */
export function rotateRuntimeSession(store: AmySessionStore, userKey: string): void {
  const key = `${SESSION_KEY_PREFIX}${userKey}`
  try {
    const raw = store.get(key)
    if (raw) {
      const stored = JSON.parse(raw) as StoredAmySession
      const conversationId = stored.conversationId ?? stored.id
      if (conversationId) {
        store.set(key, JSON.stringify({ conversationId, boundVersion: stored.boundVersion ?? null }))
        return
      }
    }
    store.remove(key)
  } catch {
    store.remove(key)
  }
}
